import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { 
  Calendar, Clock, MapPin, Users, Ticket, Download, CheckCircle, 
  ChevronLeft, X, Share2
} from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import CelebrationLasers from "@/components/common/CelebrationLasers";

const ReservationForm = ({ event, onBack }) => {
  const [formData, setFormData] = useState({
    nombre: "",
    apellidos: "",
    fecha_nacimiento: "",
    telefono: "",
    email: "",
    genero: "",
    codigo_postal: "",
    acepta_privacidad: false,
    newsletter_accepted: false  // No activado por defecto
  });
  const [reservation, setReservation] = useState(null);
  const [reservations, setReservations] = useState([]); // Multiple reservations
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ticketCount, setTicketCount] = useState({ remaining: event.remaining_tickets });
  const [eventClosed, setEventClosed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [cantidadEntradas, setCantidadEntradas] = useState(1);
  const qrRef = useRef(null);
  
  // Mesa/Reservado state
  const [showMesaForm, setShowMesaForm] = useState(false);
  const [mesaTipo, setMesaTipo] = useState('mesa'); // 'mesa' or 'reservado'
  const [mesasDisponibles, setMesasDisponibles] = useState([]);
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [mesaConsumicion, setMesaConsumicion] = useState(0);
  const [mesaMaxPersonas, setMesaMaxPersonas] = useState(6);
  const [numPersonas, setNumPersonas] = useState(1);
  const [metodoPago, setMetodoPago] = useState('local');
  const [mesaReservation, setMesaReservation] = useState(null);
  const [loadingMesa, setLoadingMesa] = useState(false);
  
  // Bonocopas state
  const [showBonocopaForm, setShowBonocopaForm] = useState(false);
  const [bonocopasInfo, setBonocopasInfo] = useState({ enabled: false, tipos: [] });
  const [tipoBonocopaSeleccionado, setTipoBonocopaSeleccionado] = useState(null);
  const [cantidadBonocopas, setCantidadBonocopas] = useState(1);
  const [loadingBonocopa, setLoadingBonocopa] = useState(false);
  
  // Get RRPP code from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  const rrppCode = urlParams.get('rrpp');

  const fetchTicketCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/reservations/count`);
      setTicketCount(response.data);
    } catch (e) {
      console.error("Error fetching ticket count:", e);
    }
  }, [event.id]);

  // Fetch tramos info
  const [tramosInfo, setTramosInfo] = useState({ enabled: false, visible: false, tramos: [], tramo_actual: null });
  
  const fetchTramosInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/tramos`);
      setTramosInfo(response.data);
    } catch (e) {
      console.error("Error fetching tramos:", e);
    }
  }, [event.id]);

  // Fetch mesas/reservados disponibles
  const fetchMesasDisponibles = useCallback(async (tipo) => {
    try {
      const endpoint = tipo === 'mesa' ? 'mesas' : 'reservados';
      const response = await axios.get(`${API}/events/${event.id}/${endpoint}/disponibles`);
      if (response.data.enabled) {
        setMesasDisponibles(response.data[tipo === 'mesa' ? 'mesas' : 'reservados'] || []);
        setMesaConsumicion(response.data.consumicion_minima || 0);
        setMesaMaxPersonas(response.data.max_personas || 6);
      } else {
        setMesasDisponibles([]);
        setMesaConsumicion(0);
        setMesaMaxPersonas(6);
      }
    } catch (e) {
      console.error(`Error fetching ${tipo}:`, e);
      setMesasDisponibles([]);
    }
  }, [event.id]);

  // Fetch bonocopas info
  const fetchBonocopasInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/bonocopas/disponibles`);
      setBonocopasInfo(response.data);
    } catch (e) {
      console.error("Error fetching bonocopas:", e);
    }
  }, [event.id]);

  // Check if event has started or sales are manually closed
  useEffect(() => {
    const checkEventStatus = () => {
      // Check if sales are manually closed
      if (event.entradas_activas === false) {
        setEventClosed(true);
        return;
      }
      
      // Check if event time is close (1 minute before)
      if (event.fecha_hora_evento) {
        const eventDate = new Date(event.fecha_hora_evento);
        const now = new Date();
        const timeUntil = eventDate - now;
        setEventClosed(timeUntil < 60000); // 1 minute
      } else {
        setEventClosed(false);
      }
    };
    
    checkEventStatus();
    const interval = setInterval(checkEventStatus, 10000);
    return () => clearInterval(interval);
  }, [event.fecha_hora_evento, event.entradas_activas]);

  useEffect(() => {
    fetchTicketCount();
    fetchTramosInfo();
    if (event.bonocopas_enabled) {
      fetchBonocopasInfo();
    }
  }, [fetchTicketCount, fetchTramosInfo, fetchBonocopasInfo, event.bonocopas_enabled]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    setError("");
  };

  // Auto-capitalize name (first letter of each word)
  const handleNameChange = (e) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
    setFormData(prev => ({
      ...prev,
      nombre: value
    }));
    setError("");
  };

  // Auto-format date with slashes (DD/MM/AAAA)
  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Solo números
    if (value.length > 8) value = value.slice(0, 8);
    
    // Añadir barras automáticamente
    if (value.length >= 4) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4);
    } else if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    
    setFormData(prev => ({
      ...prev,
      fecha_nacimiento: value
    }));
    setError("");
  };

  // Handle email change
  const handleEmailChange = (e) => {
    setFormData(prev => ({
      ...prev,
      email: e.target.value
    }));
    setError("");
  };

  // Auto-fill form when email loses focus
  const handleEmailBlur = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    
    try {
      const response = await axios.get(`${API}/check-email/${encodeURIComponent(email)}`);
      if (response.data.found && response.data.user_data) {
        const userData = response.data.user_data;
        // El nombre guardado en la BD es el nombre completo: separarlo en nombre y apellidos
        let nombrePart = "";
        let apellidosPart = "";
        if (userData.nombre) {
          const parts = userData.nombre.trim().split(/\s+/);
          nombrePart = parts[0] || "";
          apellidosPart = parts.slice(1).join(" ");
        }
        setFormData(prev => ({
          ...prev,
          nombre: nombrePart || prev.nombre,
          apellidos: apellidosPart || prev.apellidos,
          fecha_nacimiento: userData.fecha_nacimiento || prev.fecha_nacimiento,
          telefono: userData.telefono || prev.telefono
        }));
      }
    } catch (e) {
      // Silently fail - user can still fill manually
      console.log("Could not fetch user data");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nombre.trim()) {
      setError("Por favor, introduce tu nombre");
      return;
    }
    if (!formData.apellidos.trim()) {
      setError("Por favor, introduce tus apellidos");
      return;
    }
    if (!formData.fecha_nacimiento) {
      setError("Por favor, introduce tu fecha de nacimiento");
      return;
    }
    
    // Validar que tenga al menos 18 años
    const dateParts = formData.fecha_nacimiento.split('/');
    if (dateParts.length === 3) {
      const birthDate = new Date(
        parseInt(dateParts[2]), // año
        parseInt(dateParts[1]) - 1, // mes (0-indexed)
        parseInt(dateParts[0]) // día
      );
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        setError("Debes ser mayor de 18 años para reservar una entrada");
        return;
      }
    } else {
      setError("Formato de fecha incorrecto. Usa DD/MM/AAAA");
      return;
    }
    
    if (!formData.telefono.trim() || formData.telefono.length < 9) {
      setError("Por favor, introduce un número de teléfono válido");
      return;
    }
    if (!formData.email.trim()) {
      setError("Por favor, introduce tu email");
      return;
    }
    if (!formData.codigo_postal.trim() || formData.codigo_postal.length < 4) {
      setError("Por favor, introduce tu código postal");
      return;
    }
    if (!formData.acepta_privacidad) {
      setError("⚠️ Debes aceptar la política de privacidad para continuar con la reserva");
      return;
    }

    setLoading(true);

    try {
      // Check if event requires payment
      if (event.pago_habilitado && event.precio > 0) {
        // Create payment checkout session
        const response = await axios.post(`${API}/payments/checkout`, {
          event_id: event.id,
          cantidad: cantidadEntradas,
          nombre: `${formData.nombre.trim()} ${formData.apellidos.trim()}`,
          fecha_nacimiento: formData.fecha_nacimiento,
          telefono: formData.telefono.trim(),
          email: formData.email.trim().toLowerCase(),
          genero: formData.genero,
          codigo_postal: formData.codigo_postal.trim(),
          acepta_privacidad: formData.acepta_privacidad,
          newsletter_accepted: formData.newsletter_accepted,
          rrpp_code: rrppCode || null,
          origin_url: window.location.origin
        });

        if (response.data.success && response.data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = response.data.checkout_url;
          return;
        }
      } else {
        // Free reservation flow
        const response = await axios.post(`${API}/reservations`, {
          event_id: event.id,
          nombre: `${formData.nombre.trim()} ${formData.apellidos.trim()}`,
          fecha_nacimiento: formData.fecha_nacimiento,
          telefono: formData.telefono.trim(),
          email: formData.email.trim().toLowerCase(),
          genero: formData.genero,
          codigo_postal: formData.codigo_postal.trim(),
          acepta_privacidad: formData.acepta_privacidad,
          newsletter_accepted: formData.newsletter_accepted,
          cantidad: cantidadEntradas,
          rrpp_code: rrppCode || null
        });

        if (response.data.success) {
          setReservation(response.data.reservation);
          setReservations(response.data.reservations || [response.data.reservation]);
          setShowCelebration(true);
          // Stop celebration after 2 seconds
          setTimeout(() => setShowCelebration(false), 2000);
          fetchTicketCount();
        }
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Error al crear la reserva. Inténtalo de nuevo.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle mesa/reservado reservation
  const handleMesaSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nombre.trim()) {
      setError("Por favor, introduce tu nombre");
      return;
    }
    if (!formData.telefono.trim() || formData.telefono.length < 9) {
      setError("Por favor, introduce un número de teléfono válido");
      return;
    }
    if (!formData.email.trim()) {
      setError("Por favor, introduce tu email");
      return;
    }
    if (!mesaSeleccionada) {
      setError(`Por favor, selecciona una ${mesaTipo === 'mesa' ? 'mesa' : 'reservado'}`);
      return;
    }

    setLoadingMesa(true);

    try {
      const response = await axios.post(`${API}/mesas/reservar`, {
        event_id: event.id,
        tipo: mesaTipo,
        numero_mesa: mesaSeleccionada,
        nombre: formData.nombre.trim(),
        email: formData.email.trim().toLowerCase(),
        telefono: formData.telefono.trim(),
        num_personas: numPersonas,
        metodo_pago: metodoPago
      });

      if (response.data.success) {
        if (response.data.requires_payment && response.data.checkout_url) {
          // Redirect to Stripe for payment
          window.location.href = response.data.checkout_url;
        } else {
          // Local payment - show success
          setMesaReservation(response.data.reservation);
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 2000);
        }
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || `Error al reservar la ${mesaTipo}. Inténtalo de nuevo.`;
      setError(errorMsg);
    } finally {
      setLoadingMesa(false);
    }
  };

  const openMesaForm = (tipo) => {
    setMesaTipo(tipo);
    setShowMesaForm(true);
    setMesaSeleccionada(null);
    setNumPersonas(1);
    setMetodoPago('local');
    setError("");
    fetchMesasDisponibles(tipo);
  };

  // Bonocopa functions
  const openBonocopaForm = () => {
    setShowBonocopaForm(true);
    setCantidadBonocopas(1);
    setTipoBonocopaSeleccionado(null);
    setError("");
    fetchBonocopasInfo();
  };

  const handleBonocopaSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nombre.trim()) {
      setError("Por favor, introduce tu nombre");
      return;
    }
    if (!formData.telefono.trim() || formData.telefono.length < 9) {
      setError("Por favor, introduce un número de teléfono válido");
      return;
    }
    if (!formData.email.trim()) {
      setError("Por favor, introduce tu email");
      return;
    }
    if (!tipoBonocopaSeleccionado) {
      setError("Por favor, selecciona un tipo de bonocopa");
      return;
    }
    if (cantidadBonocopas < 1 || cantidadBonocopas > tipoBonocopaSeleccionado.disponibles) {
      setError(`Selecciona entre 1 y ${tipoBonocopaSeleccionado.disponibles} bonocopas`);
      return;
    }

    setLoadingBonocopa(true);

    try {
      const response = await axios.post(`${API}/bonocopas/comprar`, {
        event_id: event.id,
        tipo_id: tipoBonocopaSeleccionado.id,
        nombre: formData.nombre.trim(),
        email: formData.email.trim().toLowerCase(),
        telefono: formData.telefono.trim(),
        cantidad: cantidadBonocopas
      });

      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe for payment
        window.location.href = response.data.checkout_url;
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Error al comprar el bonocopa. Inténtalo de nuevo.";
      setError(errorMsg);
    } finally {
      setLoadingBonocopa(false);
    }
  };

  const downloadQR = async () => {
    if (!qrRef.current || !reservations.length) return;
    
    const allSvgs = qrRef.current.querySelectorAll("svg");
    if (!allSvgs.length) return;

    // Create a canvas for all tickets
    const numTickets = reservations.length;
    const qrSize = 180;
    const padding = 30;
    const headerHeight = 200;
    const ticketHeight = qrSize + 80;
    const ticketsPerRow = Math.min(numTickets, 3);
    const rows = Math.ceil(numTickets / ticketsPerRow);
    
    const canvasWidth = Math.max(500, ticketsPerRow * (qrSize + padding) + padding * 2);
    const canvasHeight = headerHeight + rows * ticketHeight + 100;
    
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    
    // Background
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Header bar
    ctx.fillStyle = "#ec00e8";
    ctx.fillRect(0, 0, canvasWidth, 5);
    
    // Title
    ctx.fillStyle = "#ec00e8";
    ctx.font = "bold 28px Arial";
    ctx.textAlign = "center";
    ctx.fillText("TAYTANTOS", canvasWidth / 2, 45);
    
    // Subtitle
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "12px Arial";
    ctx.fillText("¡¡SIENTE LA MÚSICA!!", canvasWidth / 2, 65);
    
    // Event info
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 18px Arial";
    ctx.fillText(event.nombre, canvasWidth / 2, 100);
    
    ctx.fillStyle = "#00f3ff";
    ctx.font = "14px Arial";
    ctx.fillText(`${event.fecha} - ${event.hora}`, canvasWidth / 2, 125);
    
    ctx.fillStyle = "#a1a1aa";
    ctx.font = "12px Arial";
    ctx.fillText(event.sala, canvasWidth / 2, 145);
    ctx.fillText(event.direccion, canvasWidth / 2, 165);
    
    // User info
    ctx.fillStyle = "#ec00e8";
    ctx.font = "bold 14px Arial";
    ctx.fillText(`${reservation.nombre} - ${numTickets} ${numTickets > 1 ? 'entradas' : 'entrada'}`, canvasWidth / 2, 190);
    
    // Draw QR codes
    const qrPromises = Array.from(allSvgs).map((svg, index) => {
      return new Promise((resolve) => {
        const svgData = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        img.onload = () => {
          const row = Math.floor(index / ticketsPerRow);
          const col = index % ticketsPerRow;
          const totalWidthForRow = Math.min(numTickets - row * ticketsPerRow, ticketsPerRow) * (qrSize + padding) - padding;
          const startX = (canvasWidth - totalWidthForRow) / 2;
          const x = startX + col * (qrSize + padding);
          const y = headerHeight + row * ticketHeight;
          
          // QR background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(x, y, qrSize, qrSize);
          
          // Draw QR
          ctx.drawImage(img, x, y, qrSize, qrSize);
          
          // Ticket number
          ctx.fillStyle = "#ec00e8";
          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.fillText(`Entrada ${index + 1}`, x + qrSize / 2, y + qrSize + 20);
          
          // Ticket ID
          ctx.fillStyle = "#71717a";
          ctx.font = "10px Arial";
          ctx.fillText(`ID: ${reservations[index].id.slice(0, 8).toUpperCase()}`, x + qrSize / 2, y + qrSize + 40);
          
          resolve();
        };
        img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
      });
    });
    
    await Promise.all(qrPromises);
    
    // Footer
    ctx.fillStyle = "#71717a";
    ctx.font = "10px Arial";
    ctx.textAlign = "center";
    ctx.fillText("Presenta este código QR en la entrada del evento", canvasWidth / 2, canvasHeight - 30);
    
    const dataUrl = canvas.toDataURL("image/png");
    
    // Detectar iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head><title>Entradas TAYTANTOS</title></head>
            <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;">
              <div style="text-align:center;">
                <p style="color:#fff;margin-bottom:20px;font-family:sans-serif;">Mantén pulsada la imagen para guardarla</p>
                <img src="${dataUrl}" alt="Entradas TAYTANTOS" style="max-width:95vw;"/>
              </div>
            </body>
          </html>
        `);
      }
    } else {
      const link = document.createElement("a");
      link.download = `entradas-taytantos-${reservation.id.slice(0, 8)}.png`;
      link.href = dataUrl;
      link.click();
    }
  };

  const isSoldOut = ticketCount.remaining <= 0;

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10">
        {/* Back Button */}
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft size={20} />
          <span>Volver a eventos</span>
        </button>

        {/* Logo */}
        <div className="text-center mb-6 fade-in-up">
          <h1 className="font-logo text-3xl md:text-4xl text-white neon-glow">
            TAYTANTOS
          </h1>
        </div>

        {/* Price Badge */}
        <div className="text-center mb-6 fade-in-up">
          {event.pago_habilitado && event.precio > 0 ? (
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-[var(--primary)] to-[var(--accent)] text-white font-bold rounded-full text-lg pulse-glow" data-testid="price-badge">
              {event.precio.toFixed(2)} € / entrada
            </span>
          ) : (
            <span className="free-badge pulse-glow" data-testid="free-badge">
              Entrada Gratuita
            </span>
          )}
        </div>

        {/* RRPP Banner */}
        {rrppCode && (
          <div className="p-3 bg-[var(--primary)]/20 border border-[var(--primary)] text-center mb-4 fade-in-up">
            <p className="text-[var(--primary)] text-sm">
              <Share2 size={16} className="inline mr-2" />
              Invitado por un promotor TAYTANTOS
            </p>
          </div>
        )}

        {/* Event Info */}
        <div className="event-info-box mb-6 fade-in-up" data-testid="event-info-box">
          {event.flyer_url && (
            <img 
              src={event.flyer_url} 
              alt={event.nombre} 
              className="w-full h-48 object-cover mb-4"
            />
          )}
          <h2 className="font-heading text-xl text-white mb-4 uppercase tracking-wide">
            {event.nombre}
          </h2>
          <div className="space-y-3">
            <div className="event-info-item">
              <MapPin size={18} />
              <span><strong>{event.sala}</strong></span>
            </div>
            <div className="event-info-item">
              <Calendar size={18} />
              <span>{event.fecha}</span>
            </div>
            <div className="event-info-item">
              <Clock size={18} />
              <span>{event.hora}</span>
            </div>
            <div className="event-info-item">
              <MapPin size={18} />
              <span>{event.direccion}</span>
            </div>
          </div>
        </div>

        {/* Ticket Counter - Solo mostrar si NO hay tramos habilitados */}
        {!(tramosInfo.enabled && tramosInfo.visible) && (
          <div className="ticket-counter mb-6 fade-in-up" data-testid="ticket-counter">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users size={20} className="text-[var(--accent-cyan)]" />
              <span className="text-xs text-[var(--text-muted)] uppercase tracking-widest">
                Entradas Disponibles
              </span>
            </div>
            <div className="counter-value" data-testid="remaining-count">
              {ticketCount.remaining?.toLocaleString() || 0}
            </div>
            <div className="counter-label">
              de {ticketCount.max_tickets?.toLocaleString() || event.max_tickets} entradas
            </div>
          </div>
        )}

        {/* Sold Out Banner */}
        {isSoldOut && !reservation && (
          <div className="sold-out-banner mb-6" data-testid="sold-out-banner">
            Entradas Agotadas
          </div>
        )}

        {/* Event Closed Banner - Similar style to urgency banners */}
        {eventClosed && !isSoldOut && !reservation && (
          <div className="p-4 bg-orange-900/50 border-2 border-orange-500 text-center mb-6" data-testid="event-closed-banner">
            <div className="flex items-center justify-center gap-3 text-orange-400 font-bold text-lg uppercase tracking-wider">
              <span className="inline-block w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
              Entradas no disponibles
              <span className="inline-block w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
            </div>
            <p className="text-orange-300 text-sm mt-2">
              {event.mensaje_desactivacion || 'La venta de entradas para este evento ha finalizado'}
            </p>
          </div>
        )}

        {/* Tramo Info for Client */}
        {tramosInfo.enabled && tramosInfo.visible && !reservation && !eventClosed && (() => {
          // Filtrar solo tramos activos
          const tramosActivos = tramosInfo.tramos.filter(t => t.activo);
          // Verificar si hay algún tramo con pocas entradas (menos de 150)
          const hayPocasEntradas = tramosActivos.some(t => !t.agotado && t.disponibles <= 150 && t.disponibles > 0);
          // Verificar si hay algún tramo muy urgente (menos de 40)
          const hayMuyPocas = tramosActivos.some(t => !t.agotado && t.disponibles <= 40 && t.disponibles > 0);
          const todosAgotados = tramosActivos.every(t => t.agotado);
          
          return (
            <div className="mb-6 space-y-3" data-testid="tramo-info">
              {/* Banner global de pocas entradas */}
              {hayMuyPocas && !todosAgotados && (
                <div className="p-3 bg-red-900/30 border border-red-500 text-center mb-4 animate-pulse">
                  <div className="flex items-center justify-center gap-2 text-red-400 font-bold text-sm uppercase tracking-wider">
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                    ¡Últimas entradas disponibles!
                  </div>
                </div>
              )}
              {hayPocasEntradas && !hayMuyPocas && !todosAgotados && (
                <div className="p-3 bg-orange-900/30 border border-orange-500 text-center mb-4">
                  <div className="flex items-center justify-center gap-2 text-orange-400 font-bold text-sm uppercase tracking-wider">
                    <span className="inline-block w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                    ¡Quedan pocas entradas disponibles!
                  </div>
                </div>
              )}
              
              {/* Mostrar TODOS los tramos activos - Versión compacta */}
              <div className="flex flex-wrap gap-2">
                {tramosActivos.map((tramo, idx) => {
                  const disponibles = tramo.disponibles || 0;
                  const esAgotado = tramo.agotado || disponibles <= 0;
                  const esMuyUrgente = !esAgotado && disponibles <= 40;
                  const esUrgente = !esAgotado && disponibles <= 150;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`px-3 py-2 border-l-2 text-sm ${
                        esAgotado 
                          ? 'bg-gray-800/50 border-gray-500 opacity-60'
                          : esMuyUrgente 
                            ? 'bg-red-900/30 border-red-500' 
                            : esUrgente 
                              ? 'bg-orange-900/30 border-orange-500' 
                              : 'bg-[var(--surface)] border-[var(--accent-cyan)]'
                      }`}
                    >
                      <span className={`font-bold ${esAgotado ? 'text-gray-400' : 'text-white'}`}>
                        {tramo.nombre}
                      </span>
                      {esAgotado && (
                        <span className="ml-2 text-gray-400 text-xs">(Agotado)</span>
                      )}
                      {esMuyUrgente && !esAgotado && (
                        <span className="ml-2 text-red-400 text-xs animate-pulse">¡Pocas!</span>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Todos los tramos agotados */}
              {todosAgotados && tramosActivos.length > 0 && (
                <div className="p-4 bg-red-900/30 border-l-4 border-red-500 mt-4">
                  <p className="text-red-400 font-bold uppercase">Todos los tramos agotados</p>
                  <p className="text-red-300 text-sm mt-1">No quedan entradas disponibles</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Registration Form or Success */}
        {!reservation ? (
          !isSoldOut && !eventClosed && (
            <div className="form-section fade-in-up" data-testid="registration-form">
              <div className="flex items-center gap-3 mb-6">
                <Ticket size={24} className="text-[var(--primary)]" />
                <h3 className="form-title mb-0">Reserva tu entrada</h3>
              </div>

              {error && (
                <div className="error-message" data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="custom-input"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    required
                    data-testid="input-email"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="nombre">Nombre *</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    className="custom-input"
                    placeholder="Tu nombre"
                    value={formData.nombre}
                    onChange={handleNameChange}
                    required
                    data-testid="input-nombre"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="apellidos">Apellidos *</label>
                  <input
                    type="text"
                    id="apellidos"
                    name="apellidos"
                    className="custom-input"
                    placeholder="Tus apellidos"
                    value={formData.apellidos}
                    onChange={handleInputChange}
                    required
                    data-testid="input-apellidos"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="fecha_nacimiento">Fecha de Nacimiento * (DD/MM/AAAA)</label>
                  <input
                    type="text"
                    id="fecha_nacimiento"
                    name="fecha_nacimiento"
                    className="custom-input"
                    placeholder="DD/MM/AAAA"
                    value={formData.fecha_nacimiento}
                    onChange={handleDateChange}
                    maxLength={10}
                    required
                    data-testid="input-fecha-nacimiento"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="telefono">Teléfono * (WhatsApp)</label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    className="custom-input"
                    placeholder="600 000 000"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    required
                    data-testid="input-telefono"
                  />
                </div>

                {/* Selector de género */}
                <div className="form-group">
                  <label className="form-label">Género</label>
                  <div className="flex gap-2">
                    {[
                      { value: "hombre", label: "Hombre" },
                      { value: "mujer", label: "Mujer" }
                    ].map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, genero: option.value }))}
                        className={`flex-1 py-2 text-sm border transition-all ${
                          formData.genero === option.value
                            ? 'bg-[var(--primary)] border-[var(--primary)] text-white'
                            : 'bg-transparent border-[var(--border-color)] text-[var(--text-muted)] hover:border-[var(--primary)]'
                        }`}
                        data-testid={`btn-genero-${option.value}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Campo Código Postal */}
                <div className="form-group">
                  <label className="form-label" htmlFor="codigo_postal">Código Postal *</label>
                  <input
                    type="text"
                    id="codigo_postal"
                    name="codigo_postal"
                    className="custom-input"
                    placeholder="Ej: 41001"
                    maxLength="5"
                    value={formData.codigo_postal}
                    onChange={handleInputChange}
                    required
                    data-testid="input-codigo-postal"
                  />
                </div>

                {/* Selector de cantidad de entradas - Compacto */}
                <div className="form-group">
                  <label className="form-label text-sm">Número de Entradas</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCantidadEntradas(prev => Math.max(1, prev - 1))}
                      className="w-10 h-10 bg-[var(--surface)] border border-[var(--border-color)] text-white text-xl font-bold hover:bg-[var(--primary)] transition-colors"
                      data-testid="btn-menos-entradas"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold text-[var(--primary)] w-10 text-center" data-testid="cantidad-entradas">
                      {cantidadEntradas}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCantidadEntradas(prev => Math.min(5, prev + 1))}
                      className="w-10 h-10 bg-[var(--surface)] border border-[var(--border-color)] text-white text-xl font-bold hover:bg-[var(--primary)] transition-colors"
                      data-testid="btn-mas-entradas"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-[var(--text-muted)] text-xs mt-1">Máximo 5 entradas por usuario</p>
                </div>

                <div className={`checkbox-group p-4 border-2 transition-all ${
                  formData.acepta_privacidad 
                    ? 'border-[var(--success)] bg-[var(--success)]/10' 
                    : 'border-cyan-400 bg-cyan-400/10 animate-pulse'
                }`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="privacidad"
                      name="acepta_privacidad"
                      className="w-8 h-8 min-w-[32px] min-h-[32px] border-2 border-white bg-transparent cursor-pointer appearance-none checked:bg-[var(--primary)] checked:border-[var(--primary)] mt-0.5"
                      checked={formData.acepta_privacidad}
                      onChange={handleInputChange}
                      data-testid="checkbox-privacidad"
                    />
                    <div className="flex flex-col">
                      <label htmlFor="privacidad" className="cursor-pointer text-white text-sm leading-tight">
                        Acepto la política<br/>de privacidad
                      </label>
                      {!formData.acepta_privacidad && (
                        <p className="text-cyan-400 text-xs mt-1">
                          * Obligatorio para completar la reserva
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Newsletter Subscription */}
                <div className={`checkbox-group p-4 border-2 transition-all ${
                  formData.newsletter_accepted 
                    ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                    : 'border-[var(--text-muted)] bg-transparent'
                }`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter_accepted"
                      className="w-8 h-8 min-w-[32px] min-h-[32px] border-2 border-white bg-transparent cursor-pointer appearance-none checked:bg-[var(--primary)] checked:border-[var(--primary)] mt-0.5"
                      checked={formData.newsletter_accepted}
                      onChange={handleInputChange}
                      data-testid="checkbox-newsletter"
                    />
                    <div className="flex flex-col">
                      <label htmlFor="newsletter" className="cursor-pointer text-white text-sm leading-tight font-bold">
                        🎉 ¡Recibe tus entradas antes que nadie!
                      </label>
                      <p className="text-[var(--primary)] text-xs mt-1">
                        Suscríbete y recibe tu entrada automáticamente cuando se cree un nuevo evento
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mesa/Reservado/Bonocopa Buttons - ANTES del botón de confirmar */}
                {(event.mesas_enabled || event.reservados_enabled || event.bonocopas_enabled) && !showMesaForm && !showBonocopaForm && (
                  <div className="mt-6 pt-6 border-t border-[var(--border-color)]">
                    <p className="text-[var(--text-muted)] text-sm text-center mb-4">¿Quieres algo más?</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
                      {event.mesas_enabled && (
                        <button
                          type="button"
                          onClick={() => openMesaForm('mesa')}
                          className="px-4 py-3 bg-[var(--surface)] border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-all text-sm font-bold uppercase flex flex-col items-center"
                          data-testid="btn-reservar-mesa"
                        >
                          <span>Reservar Mesa</span>
                          <span className="text-xs opacity-75 mt-1">Mín. {event.mesas_consumicion_minima?.toFixed(2) || '0.00'} €</span>
                        </button>
                      )}
                      {event.reservados_enabled && (
                        <button
                          type="button"
                          onClick={() => openMesaForm('reservado')}
                          className="px-4 py-3 bg-[var(--surface)] border border-[var(--accent-cyan)] text-[var(--accent-cyan)] hover:bg-[var(--accent-cyan)] hover:text-black transition-all text-sm font-bold uppercase flex flex-col items-center"
                          data-testid="btn-reservar-reservado"
                        >
                          <span>Reservar Reservado</span>
                          <span className="text-xs opacity-75 mt-1">Mín. {event.reservados_consumicion_minima?.toFixed(2) || '0.00'} €</span>
                        </button>
                      )}
                      {event.bonocopas_enabled && bonocopasInfo.tipos && bonocopasInfo.tipos.some(t => t.disponibles > 0) && (
                        <button
                          type="button"
                          onClick={openBonocopaForm}
                          className="group relative px-6 py-4 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 bg-[length:200%_100%] text-black hover:bg-right transition-all duration-500 text-sm font-bold uppercase flex flex-col items-center shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)] animate-pulse hover:animate-none"
                          data-testid="btn-comprar-bonocopa"
                          style={{ backgroundPosition: 'left center' }}
                        >
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">¡OFERTA!</span>
                          <span className="text-lg tracking-wider">🍸 BONOCOPAS 🍸</span>
                          <span className="text-base font-bold mt-1">
                            {bonocopasInfo.tipos.length} tipo{bonocopasInfo.tipos.length > 1 ? 's' : ''} disponible{bonocopasInfo.tipos.length > 1 ? 's' : ''}
                          </span>
                          <span className="text-[10px] opacity-80 mt-1">*No incluye bebidas premium</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary mt-8"
                  disabled={loading || eventClosed}
                  data-testid="submit-button"
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : eventClosed ? (
                    "Reservas cerradas"
                  ) : event.pago_habilitado && event.precio > 0 ? (
                    `Pagar ${(event.precio * cantidadEntradas).toFixed(2)} €`
                  ) : (
                    "Conseguir Entrada"
                  )}
                </button>
              </form>
              
              {/* Mesa/Reservado Form */}
              {showMesaForm && !mesaReservation && (
                <div className="mt-8 pt-6 border-t border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold uppercase">
                      Reservar {mesaTipo === 'mesa' ? 'Mesa' : 'Reservado'}
                    </h3>
                    <button
                      onClick={() => setShowMesaForm(false)}
                      className="text-[var(--text-muted)] hover:text-white"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div className="bg-[var(--surface)] p-4 border-l-4 border-[var(--accent-cyan)] mb-4">
                    <p className="text-[var(--accent-cyan)] font-bold">
                      Consumición mínima: {mesaConsumicion.toFixed(2)} €
                    </p>
                    <p className="text-[var(--text-muted)] text-sm">
                      Máximo {mesaMaxPersonas} personas por {mesaTipo}
                    </p>
                  </div>
                  
                  {/* Mesa Selection Grid */}
                  <div className="mb-4">
                    <label className="text-[var(--text-muted)] text-sm mb-2 block">
                      Selecciona {mesaTipo === 'mesa' ? 'una mesa' : 'un reservado'}:
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {mesasDisponibles.map((m) => (
                        <button
                          key={m.numero}
                          type="button"
                          onClick={() => m.disponible && setMesaSeleccionada(m.numero)}
                          disabled={!m.disponible}
                          className={`p-3 text-center font-bold transition-all ${
                            !m.disponible 
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed line-through' 
                              : mesaSeleccionada === m.numero
                                ? 'bg-[var(--primary)] text-white'
                                : 'bg-[var(--surface)] text-white border border-[var(--border-color)] hover:border-[var(--primary)]'
                          }`}
                          data-testid={`mesa-${m.numero}`}
                        >
                          {m.numero}
                        </button>
                      ))}
                    </div>
                    {mesasDisponibles.length === 0 && (
                      <p className="text-[var(--text-muted)] text-sm text-center py-4">
                        Cargando {mesaTipo}s disponibles...
                      </p>
                    )}
                  </div>
                  
                  {/* Num personas */}
                  <div className="mb-4">
                    <label className="text-[var(--text-muted)] text-sm mb-2 block">
                      Número de personas:
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNumPersonas(prev => Math.max(1, prev - 1))}
                        className="w-10 h-10 bg-[var(--surface)] border border-[var(--border-color)] text-white font-bold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center text-white font-bold text-xl">{numPersonas}</span>
                      <button
                        type="button"
                        onClick={() => setNumPersonas(prev => Math.min(mesaMaxPersonas, prev + 1))}
                        className="w-10 h-10 bg-[var(--surface)] border border-[var(--border-color)] text-white font-bold"
                      >
                        +
                      </button>
                      <span className="text-[var(--text-muted)] text-sm ml-2">persona(s)</span>
                    </div>
                  </div>
                  
                  {/* Payment Method */}
                  <div className="mb-4">
                    <label className="text-[var(--text-muted)] text-sm mb-2 block">
                      Método de pago:
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setMetodoPago('local')}
                        className={`flex-1 p-3 text-sm font-bold uppercase transition-all ${
                          metodoPago === 'local'
                            ? 'bg-[var(--primary)] text-white'
                            : 'bg-[var(--surface)] text-white border border-[var(--border-color)]'
                        }`}
                      >
                        Pago en Local
                      </button>
                      <button
                        type="button"
                        onClick={() => setMetodoPago('online')}
                        className={`flex-1 p-3 text-sm font-bold uppercase transition-all ${
                          metodoPago === 'online'
                            ? 'bg-[var(--accent-cyan)] text-black'
                            : 'bg-[var(--surface)] text-white border border-[var(--border-color)]'
                        }`}
                      >
                        Pago Online
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleMesaSubmit}
                    className="btn-primary w-full mt-4"
                    disabled={loadingMesa || !mesaSeleccionada}
                    data-testid="submit-mesa-button"
                  >
                    {loadingMesa ? (
                      <span className="spinner"></span>
                    ) : metodoPago === 'online' ? (
                      `Pagar ${mesaConsumicion.toFixed(2)} € y Reservar`
                    ) : (
                      `Reservar ${mesaTipo === 'mesa' ? 'Mesa' : 'Reservado'} #${mesaSeleccionada || '?'}`
                    )}
                  </button>
                </div>
              )}

              {/* Bonocopa Form - Multiple Types */}
              {showBonocopaForm && (
                <div className="mt-8 pt-6 border-t-2 border-yellow-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-yellow-500 font-bold uppercase text-xl flex items-center gap-2">
                      🍸 Comprar Bonocopa 🍸
                    </h3>
                    <button
                      onClick={() => {
                        setShowBonocopaForm(false);
                        setTipoBonocopaSeleccionado(null);
                      }}
                      className="text-[var(--text-muted)] hover:text-white"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Step 1: Select Bonocopa Type */}
                  {!tipoBonocopaSeleccionado && (
                    <div className="space-y-3">
                      <p className="text-[var(--text-muted)] text-sm text-center mb-4">
                        Selecciona el tipo de bonocopa que deseas:
                      </p>
                      
                      {bonocopasInfo.tipos && bonocopasInfo.tipos.filter(t => t.disponibles > 0).map((tipo) => (
                        <button
                          key={tipo.id}
                          type="button"
                          onClick={() => {
                            setTipoBonocopaSeleccionado(tipo);
                            setCantidadBonocopas(1);
                          }}
                          className="w-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-2 border-yellow-500/50 hover:border-yellow-500 p-4 text-left transition-all group"
                          data-testid={`bonocopa-tipo-${tipo.id}`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-yellow-500 font-bold text-lg group-hover:text-yellow-400">
                                🍹 {tipo.copas} COPAS
                              </p>
                              <p className="text-[var(--text-muted)] text-xs">
                                {tipo.nombre || `Bonocopa ${tipo.copas} copas`}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-black text-2xl">{tipo.precio.toFixed(2)} €</p>
                              <p className="text-green-400 text-xs">{tipo.disponibles} disponibles</p>
                            </div>
                          </div>
                        </button>
                      ))}
                      
                      <p className="text-red-400 text-xs text-center mt-4">
                        ⚠️ No incluye bebidas premium ni champagne
                      </p>
                    </div>
                  )}

                  {/* Step 2: Selected Type - Quantity */}
                  {tipoBonocopaSeleccionado && (
                    <>
                      <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-2 border-yellow-500 p-6 mb-6 text-center relative overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setTipoBonocopaSeleccionado(null)}
                          className="absolute top-2 left-2 text-[var(--text-muted)] hover:text-white text-xs underline"
                        >
                          ← Cambiar tipo
                        </button>
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400"></div>
                        <p className="text-yellow-400 font-bold text-2xl mb-2 mt-4">
                          🍹 {tipoBonocopaSeleccionado.copas} COPAS 🍹
                        </p>
                        <p className="text-4xl text-white font-black">
                          {tipoBonocopaSeleccionado.precio.toFixed(2)} €
                        </p>
                        <p className="text-green-400 text-sm mt-2 font-bold">
                          ¡Ahorra comprando por adelantado!
                        </p>
                        <p className="text-[var(--text-muted)] text-xs mt-3">
                          {tipoBonocopaSeleccionado.disponibles} disponibles
                        </p>
                        <div className="mt-3 pt-3 border-t border-yellow-500/30">
                          <p className="text-red-400 text-xs font-medium">
                            ⚠️ No incluye bebidas premium ni champagne
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="form-label">Cantidad de Bonocopas</label>
                          <div className="flex items-center gap-4 justify-center">
                            <button
                              type="button"
                              onClick={() => setCantidadBonocopas(Math.max(1, cantidadBonocopas - 1))}
                              className="w-14 h-14 bg-[var(--surface)] border-2 border-yellow-500/50 text-yellow-500 text-2xl font-bold hover:bg-yellow-500 hover:text-black transition-colors rounded"
                              disabled={cantidadBonocopas <= 1}
                            >
                              -
                            </button>
                            <span className="text-white text-4xl font-black w-16 text-center">{cantidadBonocopas}</span>
                            <button
                              type="button"
                              onClick={() => setCantidadBonocopas(Math.min(tipoBonocopaSeleccionado.disponibles, cantidadBonocopas + 1))}
                              className="w-14 h-14 bg-[var(--surface)] border-2 border-yellow-500/50 text-yellow-500 text-2xl font-bold hover:bg-yellow-500 hover:text-black transition-colors rounded"
                              disabled={cantidadBonocopas >= tipoBonocopaSeleccionado.disponibles || cantidadBonocopas >= 10}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="bg-black/50 p-4 border border-yellow-500/30 rounded">
                          <div className="flex justify-between items-center">
                            <span className="text-[var(--text-muted)]">Total copas:</span>
                            <span className="text-yellow-400 font-bold text-xl">{cantidadBonocopas * tipoBonocopaSeleccionado.copas} copas 🍸</span>
                          </div>
                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-yellow-500/20">
                            <span className="text-white font-bold">TOTAL A PAGAR:</span>
                            <span className="text-yellow-500 font-black text-2xl">{(cantidadBonocopas * tipoBonocopaSeleccionado.precio).toFixed(2)} €</span>
                          </div>
                        </div>

                        <p className="text-xs text-[var(--text-muted)] text-center">
                          📱 Recibirás un QR único por cada bonocopa para canjear en la barra.
                        </p>
                      </div>

                      <button
                        onClick={handleBonocopaSubmit}
                        className="w-full mt-6 py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-bold uppercase tracking-wider transition-all"
                        disabled={loadingBonocopa}
                        data-testid="submit-bonocopa-button"
                      >
                        {loadingBonocopa ? (
                          <span className="spinner"></span>
                        ) : (
                          `Comprar ${cantidadBonocopas} Bonocopa${cantidadBonocopas > 1 ? 's' : ''} - ${(cantidadBonocopas * tipoBonocopaSeleccionado.precio).toFixed(2)} €`
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
              
              {/* Mesa Reservation Success */}
              {mesaReservation && (
                <div className="mt-8 pt-6 border-t border-[var(--border-color)]">
                  <CelebrationLasers show={showCelebration} />
                  <div className="text-center">
                    <CheckCircle size={48} className="text-[var(--success)] mx-auto mb-4" />
                    <h3 className="text-white font-bold text-xl mb-2">
                      ¡{mesaReservation.tipo === 'mesa' ? 'Mesa' : 'Reservado'} #{mesaReservation.numero_mesa} Reservado!
                    </h3>
                    <p className="text-[var(--text-muted)] mb-4">
                      Te hemos enviado un email de confirmación.
                    </p>
                    <div className="bg-[var(--surface)] p-4 text-left space-y-2">
                      <p className="text-white"><strong>ID:</strong> {mesaReservation.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-white"><strong>Personas:</strong> {mesaReservation.num_personas}</p>
                      <p className="text-white"><strong>Consumición mínima:</strong> {mesaReservation.consumicion_minima.toFixed(2)} €</p>
                      <p className="text-white"><strong>Pago:</strong> {mesaReservation.metodo_pago === 'local' ? 'En local' : 'Online'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          <>
            {/* Celebration Effect */}
            <CelebrationLasers show={showCelebration} />
            
            <div className="form-section success-section fade-in-up" data-testid="success-section">
              {/* Thank you banner */}
              <div className="w-full p-4 bg-gradient-to-r from-[var(--primary)]/20 via-[var(--primary)]/30 to-[var(--primary)]/20 border-y-2 border-[var(--primary)] mb-6">
                <p className="text-center text-[var(--primary)] font-bold text-xl uppercase tracking-widest">
                  ¡Gracias por ser TAYTANTOS!
                </p>
              </div>

              <CheckCircle size={48} className="text-[var(--success)] mx-auto mb-4" />
              <h3 className="success-title">
                {reservations.length > 1 ? `¡${reservations.length} Reservas Confirmadas!` : '¡Reserva Confirmada!'}
              </h3>
              <p className="success-subtitle">
                {reservations.length > 1 
                  ? 'Presenta estos códigos QR en la entrada' 
                  : 'Presenta este código QR en la entrada'}
              </p>
              <p className="text-[var(--primary)] text-sm mb-4">
                📧 También te hemos enviado {reservations.length > 1 ? 'los QRs' : 'el QR'} a tu email
              </p>

              {/* Show all QR codes */}
              <div className={`${reservations.length > 1 ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''} mb-6`} ref={qrRef}>
                {reservations.map((res, index) => (
                  <div 
                    key={res.id} 
                    className={`qr-container qr-holographic ${reservations.length > 1 ? 'p-4' : ''}`}
                    data-testid={`qr-code-${index}`}
                  >
                    {reservations.length > 1 && (
                      <p className="text-[var(--primary)] font-bold text-sm mb-2">
                        Entrada {index + 1} de {reservations.length}
                      </p>
                    )}
                    <QRCodeSVG
                      value={`TAYTANTOS-${res.id}`}
                      size={reservations.length > 1 ? 150 : 200}
                      level="H"
                      includeMargin={false}
                    />
                    <p className="text-[var(--text-muted)] text-xs mt-2 font-mono">
                      ID: {res.id.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                ))}
              </div>

              <div 
                onClick={downloadQR}
                className="w-full p-4 bg-[var(--primary)] hover:bg-[var(--primary)]/80 cursor-pointer transition-all mb-6 text-center"
                data-testid="download-button"
              >
                <div className="flex items-center justify-center gap-3 text-black font-bold text-lg uppercase tracking-wider">
                  <Download size={24} />
                  {reservations.length > 1 ? `Descargar ${reservations.length} Entradas` : 'Descargar Entrada'}
                  <Download size={24} />
                </div>
              </div>

              <div className="qr-info" data-testid="reservation-info">
                <div className="qr-info-row">
                  <span className="qr-info-label">Nombre</span>
                  <span className="qr-info-value">{reservation.nombre}</span>
                </div>
                <div className="qr-info-row">
                  <span className="qr-info-label">Email</span>
                  <span className="qr-info-value">{reservation.email}</span>
                </div>
                {reservations.length > 1 && (
                  <div className="qr-info-row">
                    <span className="qr-info-label">Número de Entradas</span>
                    <span className="qr-info-value text-[var(--primary)] font-bold">{reservations.length}</span>
                  </div>
                )}
                <div className="qr-info-row">
                  <span className="qr-info-label">Evento</span>
                  <span className="qr-info-value">{event.nombre}</span>
                </div>
                <div className="qr-info-row">
                  <span className="qr-info-label">Fecha</span>
                  <span className="qr-info-value">{event.fecha} - {event.hora}</span>
                </div>
                <div className="qr-info-row">
                  <span className="qr-info-label">Lugar</span>
                  <span className="qr-info-value">{event.sala}</span>
                </div>
                {reservation.tramo && tramosInfo.visible && (
                  <div className="qr-info-row">
                    <span className="qr-info-label">Tramo</span>
                    <span className="qr-info-value text-[var(--accent-cyan)]">{reservation.tramo}</span>
                  </div>
                )}
                <div className="qr-info-row">
                  <span className="qr-info-label">Dirección</span>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.direccion)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="qr-info-value text-[var(--primary)] underline"
                  >
                    {event.direccion} →
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReservationForm;
